import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Package, Plus, ArrowsClockwise, MagnifyingGlass, Check, Warning } from "@phosphor-icons/react";

export default function SendToInventoryDialog({ item, onClose, onSent }) {
  const [mode, setMode] = useState("new");
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [missing, setMissing] = useState([]);

  useEffect(() => {
    if (!item) return;
    setMode("new"); setQ(""); setSelected(null); setNote(""); setMissing([]);
    setQty(Math.max(1, Number(item.draft?.stock) || 1));
  }, [item]);

  useEffect(() => {
    if (!item || mode !== "entry") return;
    const t = setTimeout(() => {
      api.get(`/preinventory/${item.id}/matches`, { params: q.trim() ? { q: q.trim() } : {} })
        .then((r) => setMatches(r.data || []))
        .catch(() => setMatches([]));
    }, 250);
    return () => clearTimeout(t);
  }, [item, mode, q]);

  if (!item) return null;
  const readiness = item.readiness || { missing: [], ready: false, checklist_done: false };
  const blockedNew = !readiness.ready;
  const blockedEntry = !readiness.checklist_done;

  const send = async () => {
    setSending(true);
    setMissing([]);
    try {
      const body = { mode, quantity: Number(qty) || 1, note };
      if (mode === "entry") body.product_id = selected?.id;
      const { data } = await api.post(`/preinventory/${item.id}/send`, body);
      toast.success(mode === "entry"
        ? `Entrada registrada: +${data.movement.quantity} en «${data.movement.product_name}»`
        : `Producto «${data.product.name}» creado en Inventario`);
      onSent?.(data);
      onClose?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object") {
        setMissing(d.missing || []);
        toast.error(d.message || "No se pudo enviar");
      } else {
        toast.error(d || "No se pudo enviar");
      }
    } finally {
      setSending(false);
    }
  };

  const ModeCard = ({ value, icon: Icon, title, desc, blocked, testid }) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      className={`text-left border rounded-md p-3 transition-colors ${mode === value ? "border-[#002FA7] bg-[#002FA7]/5" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 font-semibold text-sm text-zinc-900">
        <Icon size={16} weight="bold" className={mode === value ? "text-[#002FA7]" : "text-zinc-500"} /> {title}
      </div>
      <div className="text-xs text-zinc-500 mt-1">{desc}</div>
      {blocked && (
        <div className="text-[10px] uppercase tracking-widest text-amber-700 mt-1.5 inline-flex items-center gap-1">
          <Warning size={11} weight="bold" /> Faltan requisitos
        </div>
      )}
    </button>
  );

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="send-inventory-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Package size={22} weight="duotone" className="text-[#002FA7]" /> Enviar a Inventario
            <span className="font-mono text-sm text-zinc-500">{item.code}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ModeCard value="new" icon={Plus} title="Producto nuevo" blocked={blockedNew} testid="send-mode-new"
              desc="Crea un producto en Inventario con toda la ficha (specs, precios, fotos, tienda)." />
            <ModeCard value="entry" icon={ArrowsClockwise} title="Entrada a producto existente" blocked={blockedEntry} testid="send-mode-entry"
              desc="No duplica: registra una entrada de stock (+N piezas) en un producto que ya existe." />
          </div>

          {mode === "entry" && (
            <div className="space-y-2" data-testid="entry-picker">
              <Label className="text-xs uppercase tracking-wider">Producto destino</Label>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, SKU o marca…" className="pl-8" data-testid="entry-search" />
              </div>
              <div className="border border-zinc-200 rounded-md divide-y divide-zinc-100 max-h-60 overflow-y-auto bg-white">
                {matches.length === 0 && <div className="text-xs text-zinc-400 italic p-3">Sin coincidencias. Prueba otro término.</div>}
                {matches.map((p) => {
                  const active = selected?.id === p.id;
                  const photo = p.photos_url || (p.photos && p.photos[0]);
                  return (
                    <button key={p.id} type="button" onClick={() => setSelected(p)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-zinc-50 ${active ? "bg-[#002FA7]/5" : ""}`}
                      data-testid={`entry-match-${p.id}`}>
                      <span className="w-9 h-9 rounded bg-zinc-100 flex items-center justify-center overflow-hidden shrink-0">
                        {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : <Package size={16} className="text-zinc-400" />}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-zinc-800 truncate">{p.name}</span>
                        <span className="block text-[11px] text-zinc-500 truncate">{[p.brand, p.category, p.sku].filter(Boolean).join(" · ")} · stock {p.stock}</span>
                      </span>
                      {active && <Check size={16} weight="bold" className="text-[#002FA7]" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Cantidad de equipos</Label>
              <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1 numeric" data-testid="send-qty" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider">Nota (opcional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej. lote de 3 equipos limpios y probados" className="mt-1" data-testid="send-note" />
            </div>
          </div>

          {(missing.length > 0 || (mode === "new" ? blockedNew : blockedEntry)) && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="send-missing">
              <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold mb-1.5">Falta para poder enviar</div>
              <div className="flex flex-wrap gap-1.5">
                {(missing.length > 0 ? missing : (mode === "new" ? readiness.missing : readiness.missing.filter((m) => m.key.startsWith("checklist_")))).map((m) => (
                  <span key={m.key} className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-amber-100 text-amber-700">{m.label}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="send-cancel">Cancelar</Button>
          <Button onClick={send} disabled={sending || (mode === "new" ? blockedNew : (blockedEntry || !selected))}
            className="bg-[#002FA7] hover:bg-[#002277] text-white gap-1.5" data-testid="send-confirm">
            <Check size={14} weight="bold" /> {sending ? "Enviando…" : mode === "entry" ? "Registrar entrada" : "Crear en Inventario"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
